import type {
  TeamScheduleCampaignMilestone,
  TeamScheduleLeave,
  TeamScheduleQuestDeadline,
  TeamScheduleResponse,
} from '@aegislab/shared';

/** 근태 달력과 같은 공휴일 소스(`GET /holidays`)의 최소 모양. */
export interface ScheduleHoliday {
  date: string;
  name: string;
  isRecurring: boolean;
}

export interface ScheduleDay {
  /** `YYYY-MM-DD` */
  date: string;
  day: number;
  /** 0=일 … 6=토 */
  weekday: number;
  holidayName: string | null;
  quests: TeamScheduleQuestDeadline[];
  /** 캠페인 끝점·하루짜리 행 — 마감으로 센다 */
  campaigns: TeamScheduleCampaignMilestone[];
  /** 캠페인 기간의 시작점 — 마감이 아니다 */
  campaignStarts: TeamScheduleCampaignMilestone[];
  leaves: TeamScheduleLeave[];
  /** 퀘스트 마감 + 캠페인 끝점·하루짜리 행 */
  deadlineCount: number;
  /** 마감 2건 이상인데 휴가자가 있는 날 */
  crunch: boolean;
  /** 휴가자가 그날 마감 퀘스트의 참여자다 */
  leaveOnDeadline: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 그 달의 첫날·마지막 날 — 조회 기간. `month` 는 1~12. */
export function monthRange(year: number, month: number): { from: string; to: string } {
  const last = new Date(year, month, 0).getDate();
  return { from: `${year}-${pad(month)}-01`, to: `${year}-${pad(month)}-${pad(last)}` };
}

/**
 * 달력 칸. 첫 주 앞의 빈 칸은 null 로 채운다(일요일 시작, 7열).
 * 세 층(퀘스트 마감·캠페인 일정·휴가)을 날짜별로 모으고 "마감이 몰린 날 사람이 비는가"를 판정한다.
 */
export function buildScheduleMonth(
  year: number,
  month: number,
  data: Pick<TeamScheduleResponse, 'leaves' | 'questDeadlines' | 'campaignMilestones'> | undefined,
  holidays: readonly ScheduleHoliday[] = []
): (ScheduleDay | null)[] {
  const holidayByDate = new Map(
    holidays.map((h) => [h.isRecurring ? `${year}-${h.date.slice(5, 10)}` : h.date, h.name])
  );
  const lastDay = new Date(year, month, 0).getDate();
  const leading = new Date(year, month - 1, 1).getDay();
  const cells: (ScheduleDay | null)[] = Array.from({ length: leading }, () => null);

  for (let day = 1; day <= lastDay; day++) {
    const date = `${year}-${pad(month)}-${pad(day)}`;
    const quests = data?.questDeadlines.filter((q) => q.date === date) ?? [];
    const points = data?.campaignMilestones.filter((c) => c.date === date) ?? [];
    const campaigns = points.filter((c) => c.edge !== 'START');
    const campaignStarts = points.filter((c) => c.edge === 'START');
    const leaves = data?.leaves.filter((l) => l.startDate <= date && l.endDate >= date) ?? [];
    const deadlineCount = quests.length + campaigns.length;
    const away = new Set(leaves.map((l) => l.userId));
    cells.push({
      date,
      day,
      weekday: (leading + day - 1) % 7,
      holidayName: holidayByDate.get(date) ?? null,
      quests,
      campaigns,
      campaignStarts,
      leaves,
      deadlineCount,
      crunch: deadlineCount >= 2 && leaves.length >= 1,
      leaveOnDeadline: quests.some((q) => q.participants.some((p) => away.has(p.userId))),
    });
  }
  return cells;
}
